/**
 * Canvas Selector Component
 *
 * Dropdown to select an exposed Canvas to route messages to.
 * When a canvas is selected, messages go through the canvas workflow instead of direct chat.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useEffect } from "react";
import type { ExposedWorkflow } from "@/app/canvas/features/master-trigger/types";
type WorkflowSelectorProps = {
  workflows: ExposedWorkflow[];
  selectedWorkflow: ExposedWorkflow | null;
  onWorkflowChange: (workflow: ExposedWorkflow | null) => void;
  isLoading?: boolean;
  disabled?: boolean;
};
const WorkflowSelector: React.FC<WorkflowSelectorProps> = t0 => {
  const $ = _c(35);
  const {
    workflows,
    selectedWorkflow,
    onWorkflowChange,
    isLoading: t1,
    disabled: t2
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const disabled = t2 === undefined ? false : t2;
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  let t3;
  let t4;
  if ($[0] !== isOpen) {
    t3 = () => {
      const handleClickOutside = event => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };
      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
      }
    };
    t4 = [isOpen];
    $[0] = isOpen;
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  let t5;
  if ($[3] !== onWorkflowChange) {
    t5 = workflow => {
      onWorkflowChange(workflow);
      setIsOpen(false);
    };
    $[3] = onWorkflowChange;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleWorkflowSelect = t5;
  let t6;
  if ($[5] !== disabled || $[6] !== isOpen) {
    t6 = () => !disabled && setIsOpen(!isOpen);
    $[5] = disabled;
    $[6] = isOpen;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = disabled || isLoading;
  const t8 = `flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs transition-colors ${selectedWorkflow ? "border-lavender/50 bg-lavender/30 text-foreground hover:bg-lavender/40" : "border-foreground/30 bg-white/60 text-foreground hover:bg-white/80"} disabled:opacity-60`;
  const t9 = selectedWorkflow ? `Canvas: ${selectedWorkflow.displayName}` : "Select a canvas";
  const t10 = `flex h-4 w-4 items-center justify-center rounded text-[10px] ${selectedWorkflow ? "bg-lavender/50 text-purple-700" : "bg-foreground/10 text-foreground/60"}`;
  let t11;
  if ($[8] !== isLoading) {
    t11 = isLoading ? <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg> : <span>C</span>;
    $[8] = isLoading;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  let t12;
  if ($[10] !== t10 || $[11] !== t11) {
    t12 = <span className={t10}>{t11}</span>;
    $[10] = t10;
    $[11] = t11;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const t13 = isLoading ? "Loading..." : selectedWorkflow ? selectedWorkflow.displayName : "Canvas";
  let t14;
  if ($[13] !== t13) {
    t14 = <span className="font-medium max-w-[120px] truncate">{t13}</span>;
    $[13] = t13;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const t15 = `h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`;
  let t16;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />;
    $[15] = t16;
  } else {
    t16 = $[15];
  }
  let t17;
  if ($[16] !== t15) {
    t17 = <svg className={t15} fill="none" stroke="currentColor" viewBox="0 0 24 24">{t16}</svg>;
    $[16] = t15;
    $[17] = t17;
  } else {
    t17 = $[17];
  }
  let t18;
  if ($[18] !== t12 || $[19] !== t14 || $[20] !== t17 || $[21] !== t6 || $[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t18 = <button type="button" onClick={t6} disabled={t7} className={t8} title={t9}>{t12}{t14}{t17}</button>;
    $[18] = t12;
    $[19] = t14;
    $[20] = t17;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== handleWorkflowSelect || $[27] !== isLoading || $[28] !== isOpen || $[29] !== selectedWorkflow || $[30] !== workflows) {
    t19 = isOpen && <div className="absolute bottom-full left-0 mb-2 w-72 rounded-lg border border-foreground/20 bg-white/90 backdrop-blur-md shadow-xl z-50 max-h-80 overflow-y-auto"><div className="p-2"><button onClick={() => handleWorkflowSelect(null)} className={`w-full rounded-md px-3 py-2 text-left transition-colors mb-2 ${!selectedWorkflow ? "bg-white/60 border border-foreground/20" : "hover:bg-white/60 border border-transparent"}`}><div className="flex items-center gap-2 mb-1"><span className="flex h-5 w-5 items-center justify-center rounded bg-foreground/10 text-foreground/60 text-xs">D</span><span className="text-xs font-medium text-foreground">Default Chat</span>{!selectedWorkflow && <span className="rounded-full bg-foreground/10 px-1.5 py-0.5 text-[9px] text-foreground/60">Active</span>}</div><div className="text-[10px] text-foreground/50 leading-tight pl-7">Standard chat - messages sent directly to AI</div></button>{workflows.length > 0 && <div className="border-t border-foreground/10 my-2" />}{workflows.length > 0 && <div className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-foreground/50">Connected Canvases ({workflows.length})</div>}<div className="space-y-1">{workflows.map(workflow_0 => <button key={`${workflow_0.canvasId}-${workflow_0.triggerNodeId}`} onClick={() => handleWorkflowSelect(workflow_0)} className={`w-full rounded-md px-3 py-2 text-left transition-colors ${selectedWorkflow?.triggerNodeId === workflow_0.triggerNodeId ? "bg-lavender/30 border border-lavender/50" : "hover:bg-white/60 border border-transparent"}`}><div className="flex items-center gap-2 mb-1"><span className="flex h-5 w-5 items-center justify-center rounded bg-lavender/30 text-purple-700 text-xs">C</span><span className="text-xs font-medium text-foreground truncate flex-1">{workflow_0.displayName}</span>{selectedWorkflow?.triggerNodeId === workflow_0.triggerNodeId && <span className="rounded-full bg-lavender/30 px-1.5 py-0.5 text-[9px] text-purple-700">Active</span>}</div>{workflow_0.description && <div className="text-[10px] text-foreground/50 leading-tight pl-7 truncate">{workflow_0.description}</div>}<div className="flex items-center gap-3 mt-1 pl-7 text-[9px] text-foreground/40"><span>Canvas: {workflow_0.canvasName}</span>{workflow_0.triggerCount !== undefined && workflow_0.triggerCount > 0 && <span>Used {workflow_0.triggerCount}x</span>}</div></button>)}</div>{workflows.length === 0 && !isLoading && <div className="text-center py-4 text-xs text-foreground/50">No canvases available.<br /><span className="text-[10px]">Enable Master Trigger nodes in your canvases to use them here.</span></div>}</div></div>;
    $[26] = handleWorkflowSelect;
    $[27] = isLoading;
    $[28] = isOpen;
    $[29] = selectedWorkflow;
    $[30] = workflows;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== t18 || $[33] !== t19) {
    t20 = <div className="relative" ref={dropdownRef}>{t18}{t19}</div>;
    $[32] = t18;
    $[33] = t19;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  return t20;
};
export default WorkflowSelector;